<script lang="ts" setup>
import { PhArrowRight } from '@phosphor-icons/vue'
import { computed } from 'vue'

import { BlockContainer, Button, TableOfContent, TableOfContentBody, TableOfContentHeader } from '@toni/brand-ui'
import { usePageStore } from '@toni/rendering/src/stores/page'

import { usePageBlockWidth } from '../../composables/use-page-block-width.ts'
import { useContentBlock } from '../../content-blocks/composables/use-content-block'

const props = defineProps<{ id: string }>()

const isHorizontal = computed(() => contentBlock.value.data.tableOfContents.items.length <= 5)

const pageStore = usePageStore()
const brand = computed(() => pageStore.brandFromStore as string)
const isHome = computed(() => pageStore.isHome as boolean)
const size = computed(() => usePageBlockWidth(pageStore.pageData?.pageMeta, isHome.value, brand.value).value)
const id = computed(() => props.id)
const contentBlock = useContentBlock(id.value, 'tableOfContents')

const scrollToAnchor = (href: string) => {
  const headerHeightWithOffset = 90
  const element = document.querySelector(href)
  if (element) {
    const elementPosition = element.getBoundingClientRect().top
    const offsetPosition = elementPosition + window.scrollY - headerHeightWithOffset

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    })
  }
}
</script>

<template>
  <BlockContainer :size="size"
                  class="mb-6 md:mb-8">
    <TableOfContent class="gap-6 md:gap-8">
      <TableOfContentHeader>
        {{ contentBlock.data.tableOfContents.title }}
      </TableOfContentHeader>
      <TableOfContentBody class="block md:hidden flex-col gap-4">
        <Button
          v-for="item in contentBlock.data.tableOfContents.items"
          :key="item.href"
          :variant="'link'"
          :size="'sm'"
          :class="
            'max-w-full'"
          @click="scrollToAnchor(item.href)">
          <span class="truncate overflow-hidden whitespace-nowrap">
            {{ item.text }}
          </span>
          <span
            class="flex-shrink-0">
            <PhArrowRight
              weight="bold"
              :class="'w-4 h-4'" />
          </span>
        </Button>
      </TableOfContentBody>
      <TableOfContentBody class="group [&:has(>:nth-child(6))]:flex-col flex-row flex-wrap gap-5 hidden md:flex">
        <Button
          v-for="(item, index) in contentBlock.data.tableOfContents.items"
          :key="item.href"
          :variant="'link'"
          :size="'lg'"
          :class="[
            'max-w-full',
            isHorizontal && index !== contentBlock.data.tableOfContents.items.length - 1
              ? 'relative mr-3 after:absolute after:block after:bg-neutral-200 after:-right-4.5 after:top-[40%] after:w-1.5 after:h-1.5 after:rounded-full'
              : ''
          ]"
          @click="scrollToAnchor(item.href)">
          <span class="truncate overflow-hidden whitespace-nowrap">
            {{ item.text }}
          </span>
          <span
            class="hidden group-[&:has(>:nth-child(5))]:block shrink-0">
            <PhArrowRight v-if="!isHorizontal"
                          weight="bold"
                          :class="'w-5 h-5'" />
          </span>
        </Button>
      </TableOfContentBody>
    </TableOfContent>
  </BlockContainer>
</template>
